import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import {
  Save,
  Refresh,
  SmartToy,
  Notifications,
  Security,
  Person,
} from '@mui/icons-material';

export default function Settings() {
  const [settings, setSettings] = useState({
    notifications: {
      emailAlerts: true,
      smsAlerts: false,
      workflowUpdates: true,
      systemMaintenance: true,
    },
    automation: {
      autoAssignCases: true,
      autoEscalation: true,
      autoReminders: true,
      batchProcessing: false,
    },
    communication: {
      maxDailyContacts: 3,
      cooldownPeriod: 24,
      businessHoursOnly: true,
      weekendsEnabled: false,
    },
  });

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSettingChange = (category: string, setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: value
      }
    }));
  };

  const handleSave = () => {
    // Simulate saving settings
    setMessage({ type: 'success', text: 'Settings saved successfully!' });
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        ⚙️ System Settings
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure system behavior and preferences
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* User Profile */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<Person />}
              title="User Profile"
              subheader="Demo mode - No authentication required"
            />
            <CardContent>
              <TextField
                fullWidth
                label="Username"
                value="demo-user"
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                label="Role"
                value="System Administrator"
                disabled
                margin="normal"
              />
              <Chip label="Demo Mode" color="info" sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<Notifications />}
              title="Notifications"
              subheader="Configure alert preferences"
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.emailAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'emailAlerts', e.target.checked)}
                  />
                }
                label="Email Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.smsAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'smsAlerts', e.target.checked)}
                  />
                }
                label="SMS Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.workflowUpdates}
                    onChange={(e) => handleSettingChange('notifications', 'workflowUpdates', e.target.checked)}
                  />
                }
                label="Workflow Updates"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.systemMaintenance}
                    onChange={(e) => handleSettingChange('notifications', 'systemMaintenance', e.target.checked)}
                  />
                }
                label="System Maintenance"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Automation Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<SmartToy />}
              title="Automation"
              subheader="Configure AI agent behavior"
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.automation.autoAssignCases}
                    onChange={(e) => handleSettingChange('automation', 'autoAssignCases', e.target.checked)}
                  />
                }
                label="Auto-assign Cases"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.automation.autoEscalation}
                    onChange={(e) => handleSettingChange('automation', 'autoEscalation', e.target.checked)}
                  />
                }
                label="Auto-escalation"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.automation.autoReminders}
                    onChange={(e) => handleSettingChange('automation', 'autoReminders', e.target.checked)}
                  />
                }
                label="Auto-reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.automation.batchProcessing}
                    onChange={(e) => handleSettingChange('automation', 'batchProcessing', e.target.checked)}
                  />
                }
                label="Batch Processing"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Communication Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<Security />}
              title="Communication Limits"
              subheader="Configure outreach constraints"
            />
            <CardContent>
              <TextField
                fullWidth
                label="Max Daily Contacts per Customer"
                type="number"
                value={settings.communication.maxDailyContacts}
                onChange={(e) => handleSettingChange('communication', 'maxDailyContacts', parseInt(e.target.value))}
                margin="normal"
                inputProps={{ min: 1, max: 10 }}
              />
              <TextField
                fullWidth
                label="Cooldown Period (hours)"
                type="number"
                value={settings.communication.cooldownPeriod}
                onChange={(e) => handleSettingChange('communication', 'cooldownPeriod', parseInt(e.target.value))}
                margin="normal"
                inputProps={{ min: 1, max: 168 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.communication.businessHoursOnly}
                    onChange={(e) => handleSettingChange('communication', 'businessHoursOnly', e.target.checked)}
                  />
                }
                label="Business Hours Only"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.communication.weekendsEnabled}
                    onChange={(e) => handleSettingChange('communication', 'weekendsEnabled', e.target.checked)}
                  />
                }
                label="Weekend Communications"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                Save Configuration
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  sx={{ mr: 2 }}
                  onClick={() => window.location.reload()}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                >
                  Save Settings
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}